import { ref, computed } from 'vue';
import { useAuth } from '@/features/auth/composables/useAuth';
import { useUserOperations } from './useUserOperations';
import { useUserPreferences } from './useUserPreferences';
import type { UserUpdate } from '@/types/auth';

export function useUserProfile() {
  const { user: currentUser } = useAuth();
  const userOperations = useUserOperations();

  // Reactive state
  const isEditing = ref(false);
  const activeTab = ref('profile');

  // Computed properties
  const isLoading = computed(() => userOperations.isLoading.value);
  const hasError = computed(() => userOperations.hasError.value);
  const error = computed(() => userOperations.error.value);
  const updateSuccess = computed(() => userOperations.updateSuccess.value);

  // Methods
  const updateProfile = async (userData: Partial<UserUpdate>) => {
    return await userOperations.updateUserProfile(userData);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    return await userOperations.changePassword(currentPassword, newPassword);
  };

  const changeEmail = async (newEmail: string) => {
    return await userOperations.updateEmail(newEmail);
  };

  const deleteAccount = async (password: string) => {
    await userOperations.deleteCurrentUser(password);
  };

  const toggleEdit = () => {
    isEditing.value = !isEditing.value;
    if (!isEditing.value) {
      userOperations.clearError();
      userOperations.clearUpdateSuccess();
    }
  };

  const setActiveTab = (tab: string) => {
    activeTab.value = tab;
    userOperations.clearError();
    userOperations.clearUpdateSuccess();
  };

  const clearMessages = () => {
    userOperations.clearError();
    userOperations.clearUpdateSuccess();
  };

  return {
    // State
    isEditing,
    activeTab,

    // Computed
    currentUser,
    isLoading,
    hasError,
    error,
    updateSuccess,

    // Methods
    updateProfile,
    changePassword,
    changeEmail,
    deleteAccount,
    toggleEdit,
    setActiveTab,
    clearMessages,
  };
}

export function useUserProfilePreferences() {
  const userPreferences = useUserPreferences();

  // Computed properties
  const preferences = computed(() => userPreferences.preferences.value);

  // Methods
  const updatePreferences = (newPreferences: Partial<typeof preferences.value>) => {
    userPreferences.updatePreferences(newPreferences);
  };

  const updateTheme = (theme: 'light' | 'dark' | 'auto') => {
    console.log('🎨 Updating theme to:', theme);
    updatePreferences({ theme });
    // Apply theme to document
    applyTheme(theme);
  };

  const updateNotifications = (notifications: Partial<typeof preferences.value.notifications>) => {
    updatePreferences({
      notifications: { ...preferences.value.notifications, ...notifications },
    });
  };

  const togglePrivacy = (key: keyof typeof preferences.value.privacy) => {
    updatePreferences({
      privacy: {
        ...preferences.value.privacy,
        [key]: !preferences.value.privacy[key],
      },
    });
  };

  const toggleAccessibility = (key: keyof typeof preferences.value.accessibility) => {
    updatePreferences({
      accessibility: {
        ...preferences.value.accessibility,
        [key]: !preferences.value.accessibility[key],
      },
    });
  };

  const applyTheme = (theme: 'light' | 'dark' | 'auto') => {
    const root = document.documentElement;
    console.log('🎨 Applying theme:', theme, 'Current classes:', root.classList.toString());

    if (theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
      console.log(
        '🎨 Auto theme applied - prefers dark:',
        prefersDark,
        'Classes now:',
        root.classList.toString()
      );
    } else {
      root.classList.toggle('dark', theme === 'dark');
      console.log(
        '🎨 Manual theme applied - is dark:',
        theme === 'dark',
        'Classes now:',
        root.classList.toString()
      );
    }
  };

  // Initialize theme on composable creation
  applyTheme(preferences.value.theme);

  return {
    // Computed
    preferences,

    // Methods
    updatePreferences,
    updateTheme,
    updateNotifications,
    togglePrivacy,
    toggleAccessibility,
    applyTheme,
  };
}

export function useUserAvatar() {
  const { user } = useAuth();

  const generateInitials = (email: string): string => {
    if (!email) return '?';

    const emailParts = email.split('@')[0];
    const initials = emailParts.charAt(0).toUpperCase();

    // Try to get a second initial if there are dots or underscores in the email prefix
    const secondChar = emailParts.includes('.')
      ? emailParts.split('.')[1]?.charAt(0)
      : emailParts.includes('_')
        ? emailParts.split('_')[1]?.charAt(0)
        : emailParts.charAt(1);

    return secondChar ? initials + secondChar.toUpperCase() : initials;
  };

  const getAvatarUrl = (): string | null => {
    // For now, we'll return null since we don't have avatar upload functionality
    // This could be extended to support Gravatar or uploaded avatars
    return null;
  };

  const userInitials = computed(() => {
    return user.value?.email ? generateInitials(user.value.email) : '?';
  });

  const avatarUrl = computed(() => getAvatarUrl());

  return {
    userInitials,
    avatarUrl,
    generateInitials,
    getAvatarUrl,
  };
}
